import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell } from 'recharts';
import { AlertCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
interface CaptiveUnderwritingPricingProps {
  onNavigate?: (dashboard: string) => void;
}
// Mock Data
const rateChangeData = [{
  class: 'HGV',
  change: 5.2,
  lossCost: 2.1
}, {
  class: 'Van',
  change: 3.8,
  lossCost: 4.5
}, {
  class: 'Car',
  change: -1.2,
  lossCost: -0.5
}, {
  class: 'Special',
  change: 8.5,
  lossCost: 6.2
}];
const scatterData = [{
  premium: 1200,
  lossCost: 800,
  class: 'HGV',
  risk: 'High'
}, {
  premium: 900,
  lossCost: 400,
  class: 'Van',
  risk: 'Low'
}, {
  premium: 1500,
  lossCost: 1100,
  class: 'HGV',
  risk: 'High'
}, {
  premium: 600,
  lossCost: 300,
  class: 'Car',
  risk: 'Low'
}, {
  premium: 2000,
  lossCost: 1800,
  class: 'Special',
  risk: 'Critical'
}, {
  premium: 1100,
  lossCost: 700,
  class: 'Van',
  risk: 'Medium'
}];
const authorityBreaches = [{
  id: 1,
  binder: 'MGA Alpha',
  type: 'Limit Excess',
  count: 2,
  status: 'Open'
}, {
  id: 2,
  binder: 'MGA Beta',
  type: 'Territory',
  count: 1,
  status: 'Resolved'
}, {
  id: 3,
  binder: 'MGA Alpha',
  type: 'Rate Deviation',
  count: 5,
  status: 'Review'
}];
const COLORS = {
  rate: '#EC4899',
  cost: '#9333EA',
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#22C55E'
};
export function CaptiveUnderwritingPricing({
  onNavigate
}: CaptiveUnderwritingPricingProps) {
  return <div className="space-y-6 animate-in" data-id="element-556">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-557">
        <div data-id="element-558">
          <h1 className="text-2xl font-bold text-black" data-id="element-559">
            Underwriting & Pricing
          </h1>
          <p className="text-neutral-500 mt-1" data-id="element-560">
            Rate adequacy, loss costs, and authority monitoring
          </p>
        </div>
      </div>

      {/* Top Row: Underwriting KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-561">
        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-562">
          <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-563">
            Rate Adequacy Index
          </p>
          <div className="flex items-center gap-2 mt-1" data-id="element-564">
            <h3 className="text-2xl font-bold text-black" data-id="element-565">1.12</h3>
            <span className="flex items-center text-sm text-green-600" data-id="element-566">
              <ArrowUpRight size={16} data-id="element-567" /> Adequate
            </span>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-568">
            Premium / Loss Cost
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-569">
          <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-570">
            Exposure Growth
          </p>
          <div className="flex items-center gap-2 mt-1" data-id="element-571">
            <h3 className="text-2xl font-bold text-black" data-id="element-572">+14.2%</h3>
            <span className="flex items-center text-sm text-purple-600" data-id="element-573">
              <ArrowUpRight size={16} data-id="element-574" /> YoY
            </span>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-575">
            Vehicle Count Growth
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-576">
          <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-577">
            Concentration Ratio
          </p>
          <div className="flex items-center gap-2 mt-1" data-id="element-578">
            <h3 className="text-2xl font-bold text-black" data-id="element-579">68%</h3>
            <span className="flex items-center text-sm text-neutral-400" data-id="element-580">
              Top 10 Fleets / Total
            </span>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-581">
            Monitor: &gt;75% = concentrated
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-582">
        {/* Rate Change Chart */}
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-583">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-584">
            Rate Change vs Loss Cost by Class
          </h3>
          <div className="h-80" data-id="element-585">
            <ResponsiveContainer width="100%" height="100%" data-id="element-586">
              <BarChart data={rateChangeData} data-id="element-587">
                <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-588" />
                <XAxis dataKey="class" data-id="element-589" />
                <YAxis unit="%" data-id="element-590" />
                <Tooltip data-id="element-591" />
                <Legend data-id="element-592" />
                <Bar dataKey="change" name="Rate Change %" fill={COLORS.rate} data-id="element-593" />
                <Bar dataKey="lossCost" name="Loss Cost %" fill={COLORS.cost} data-id="element-594" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Scatter Plot */}
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-595">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-596">
            Premium vs Loss Cost Analysis
          </h3>
          <div className="h-80" data-id="element-597">
            <ResponsiveContainer width="100%" height="100%" data-id="element-598">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-599">
                <CartesianGrid data-id="element-600" />
                <XAxis type="number" dataKey="premium" name="Premium" unit="£" data-id="element-601" />
                <YAxis type="number" dataKey="lossCost" name="Loss Cost" unit="£" data-id="element-602" />
                <Tooltip cursor={{
                strokeDasharray: '3 3'
              }} data-id="element-603" />
                <Scatter name="Policies" data={scatterData} fill="#8884d8" data-id="element-604">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.risk === 'Low' ? COLORS.low : entry.risk === 'Medium' ? COLORS.medium : COLORS.high} data-id="element-605" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Authority Breaches */}
      <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-606">
        <h3 className="text-lg font-medium text-black mb-4" data-id="element-607">
          Authority Breaches & Concentration Flags
        </h3>
        <div className="overflow-x-auto" data-id="element-608">
          <table className="w-full text-sm text-left" data-id="element-609">
            <thead className="bg-neutral-50 text-neutral-500 font-medium" data-id="element-610">
              <tr data-id="element-611">
                <th className="px-4 py-2" data-id="element-612">Binder / MGA</th>
                <th className="px-4 py-2" data-id="element-613">Breach Type</th>
                <th className="px-4 py-2" data-id="element-614">Count</th>
                <th className="px-4 py-2" data-id="element-615">Status</th>
                <th className="px-4 py-2" data-id="element-616">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100" data-id="element-617">
              {authorityBreaches.map(item => <tr key={item.id} data-id="element-618">
                  <td className="px-4 py-3 font-medium" data-id="element-619">{item.binder}</td>
                  <td className="px-4 py-3" data-id="element-620">{item.type}</td>
                  <td className="px-4 py-3" data-id="element-621">{item.count}</td>
                  <td className="px-4 py-3" data-id="element-622">
                    <span className={`px-2 py-1 rounded-full text-xs ${item.status === 'Open' ? 'bg-red-100 text-red-800' : item.status === 'Resolved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-623">
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-624">
                    <button className="text-pink-600 hover:text-pink-700 font-medium" data-id="element-625">
                      Review
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}